import React from "react";
import { t, PERIODS } from "../../lib/timing-i18n";

export function PositionPill({ positions }) {
    const primary = (positions && positions[0]) || "";
    const color = {
        GKP: "text-fa-cream border-fa-cream/40",
        DEF: "text-fa-mint border-fa-mint/40",
        MID: "text-fa-sky border-fa-sky/40",
        FWD: "text-fa-yellow border-fa-yellow/40",
    }[primary] || "text-white/50 border-white/20";
    return (
        <span className={`font-mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 border ${color}`}>
            {(positions || []).join("/") || "—"}
        </span>
    );
}

export function MetricPill({ label, value, accent, rank }) {
    const color = {
        yellow: "text-fa-yellow border-fa-yellow/40",
        mint: "text-fa-mint border-fa-mint/40",
        sky: "text-fa-sky border-fa-sky/40",
    }[accent] || "text-white border-white/25";
    // Rank badge: uses percentile to colour-code (top 10 % gold, top 30 % green,
    // bottom 30 % red, otherwise neutral). Only rendered when we have valid rank data.
    let rankBadge = null;
    if (rank && rank.rank != null && rank.total) {
        const p = rank.percentile ?? 0;
        const badgeColor = p >= 90 ? "text-fa-yellow border-fa-yellow/40 bg-fa-yellow/10"
            : p >= 70 ? "text-fa-mint border-fa-mint/40 bg-fa-mint/10"
            : p >= 30 ? "text-white/60 border-white/20 bg-white/[0.03]"
            : "text-orange-300 border-orange-400/40 bg-orange-500/10";
        const label2 = p >= 90 ? "Elite" : p >= 70 ? "Silný" : p >= 30 ? "Průměr" : "Slabý";
        rankBadge = (
            <div className={`font-mono text-[8px] uppercase tracking-widest border px-1 ${badgeColor}`}
                 title={`Rank ${rank.rank}/${rank.total} mezi ${rank.position === "ALL" ? "všemi hráči" : rank.position + " hráči"} · max ${rank.max?.toFixed?.(1)} · median ${rank.median?.toFixed?.(1)}`}>
                {label2} · top {(100 - p).toFixed(0)}%
            </div>
        );
    }
    return (
        <div className={`border ${color} bg-black/30 px-2 py-1 min-w-[80px]`} data-testid={`metric-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="font-mono text-[8px] uppercase tracking-widest text-white/50">{label}</div>
            <div className="font-display font-black text-base tabular-nums leading-tight">{value}</div>
            {rank && rank.max != null && (
                <div className="font-mono text-[7px] text-white/40 tabular-nums leading-tight">
                    max {rank.max.toFixed(1)}
                </div>
            )}
            {rankBadge && <div className="mt-0.5">{rankBadge}</div>}
        </div>
    );
}

export function SeasonPicker({ seasons, active, onToggle, lang }) {
    return (
        <div className="border border-white/10 bg-[#0d0d0d] p-3" data-testid="timing-season-picker">
            <div className="font-mono text-[9px] uppercase tracking-widest text-white/45 mb-2">{t("seasons", lang)}</div>
            <div className="flex gap-1.5 flex-wrap">
                {seasons.map((s) => {
                    const on = active.includes(s);
                    return (
                        <button
                            key={s}
                            type="button"
                            onClick={() => onToggle(s)}
                            data-testid={`timing-season-toggle-${s}`}
                            className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 border ${on ? "bg-fa-yellow text-black border-fa-yellow" : "border-white/20 text-white/60 hover:text-white"}`}
                        >
                            {s}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

export function TimingBreakdown({ timing, lang }) {
    if (!timing) return null;
    const maxPpg = Math.max(0.1, ...PERIODS.map((p) => timing[p.key]?.ppg || 0));
    return (
        <div className="border border-white/10 bg-[#0d0d0d] p-4" data-testid="timing-breakdown">
            <h3 className="font-display font-black text-sm uppercase tracking-widest mb-3">
                {t("timingBreakdown", lang)}
            </h3>
            <div className="space-y-2">
                {PERIODS.map((p) => {
                    const bucket = timing[p.key] || {};
                    const ppg = bucket.ppg || 0;
                    const w = Math.round((ppg / maxPpg) * 100);
                    return (
                        <div key={p.key} data-testid={`timing-bucket-${p.key}`}>
                            <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-widest mb-0.5">
                                <span className="text-white/70">{t(p.labelKey, lang)}</span>
                                <span className="text-white/50 tabular-nums">
                                    {bucket.points || 0}p · {bucket.played || 0} GW · <span className="text-fa-yellow font-bold">{ppg.toFixed(2)}</span> PPG
                                </span>
                            </div>
                            <div className="h-2 bg-white/[0.04] border border-white/10 relative">
                                <div
                                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-fa-mint via-fa-sky to-fa-yellow"
                                    style={{ width: `${w}%` }}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
